import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import Layout from '../../component/layout'; 
import { makeRequest2 } from '../../../../utils/makeRequest'; 
import { toastify } from '../../../../utils/toast'; 
import { useFacilityStore } from '../../../../app/z-store/store'; 
import { getSuppliersUrl, getSupplierUrl, updateSupplierURL } from "../../../../utils/urls"; 

const SuppliersManagement = () => {
  const navigate = useNavigate();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // State
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('ACTIVE'); 
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });

  // Edit Modal State
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    contactPersonName: '',
    departmentName: '',
    taxIdentificationNumber: ''
  });
  const [documentFiles, setDocumentFiles] = useState({});

  // Status options for filter tabs
  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'INACTIVE', value: 'INACTIVE' },
  ];

  // Fetch suppliers whenever the facility changes or the active tab changes
  useEffect(() => {
    if (selectedFacilityId) {
      fetchSuppliers();
    }
  }, [selectedFacilityId, activeTab]);

  // Updated fetch function to match the response structure
  const fetchSuppliers = async () => {
    if (!selectedFacilityId) return;
    try {
      setLoading(true);
      
      // Build the base URL
      let url = `${getSuppliersUrl}/${selectedFacilityId}`;
      
      let statusQuery = '';
      if (activeTab === 'ACTIVE') {
        statusQuery = 'active';
      } else if (activeTab === 'INACTIVE') {
        statusQuery = 'inactive';
      }
      
      // Append query parameter if statusQuery is defined
      if (statusQuery) {
        url += `?status=${statusQuery}`;
      }
  
      const response = await makeRequest2(url, 'GET');
      
      // Check if response is successful and has data
      if (response.success && response.data && response.data.message === "Suppliers retrieved successfully") {
        
        setSuppliers(response.data.data || []);
        
        setPagination(response.data.pagination || {
          total: 0,
          page: 1,
          limit: 10,
          pages: 0
        });
      } else {
        console.log('No suppliers found or error in response:', response);
        setSuppliers([]);
        setPagination({
          total: 0,
          page: 1,
          limit: 10,
          pages: 0
        });
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      setSuppliers([]);
      setPagination({
        total: 0,
        page: 1,
        limit: 10,
        pages: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (value) => {
    setActiveTab(value);
  };

  // Search + filtering - updated for the new data structure
  const filteredSuppliers = Array.isArray(suppliers)
    ? suppliers.filter((item) => {
        const name = (item.name || '').toLowerCase();
        const email = (item.email || '').toLowerCase();
        const department = (item.department?.name || '').toLowerCase();
        const contactPerson = (item.contactPerson?.name || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        return (
          name.includes(search) ||
          email.includes(search) ||
          department.includes(search) ||
          contactPerson.includes(search)
        );
      })
    : [];

  // Template for contact person column
  const contactPersonTemplate = (rowData) => {
    return rowData.contactPerson?.name || 'N/A';
  };

  // Template for status column with styling
  const statusTemplate = (rowData) => {
    const statusStyles = {
      'active': { color: '#28a745', backgroundColor: '#e9fbe9' },
      'archived': { color: '#6c757d', backgroundColor: '#f1f1f1' }
    };
    
    const status = rowData.status?.toLowerCase() || 'active';
    const style = statusStyles[status] || { color: '#6c757d', backgroundColor: '#f1f1f1' };
    
    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block',
        }}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Action menu in each row
  const actionTemplate = (rowData) => (
    <div className="dropdown">
      <button
        className="btn btn-primary dropdown-toggle"
        type="button"
        id={`action-${rowData._id}`}
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        Action
      </button>
      <ul className="dropdown-menu" aria-labelledby={`action-${rowData._id}`}>
        <li>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => handleViewSupplier(rowData._id)}
          >
            View
          </button>
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => handleEditSupplier(rowData._id)}
          >
            Edit
          </button>
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => handleArchiveSupplier(rowData._id)}
          >
            Archive
          </button>
        </li>
      </ul>
    </div>
  );

  // Row-level actions
  const handleViewSupplier = (id) => {
    navigate(`/facility/procurement_management/supplier/view/${id}`);
  };

  const fetchSupplierForEdit = async (id) => {
    try {
      setEditLoading(true);
      const response = await makeRequest2(
        `${getSupplierUrl}/${selectedFacilityId}/${id}`,
        'GET'
      );
      
      if (response.success && response.data && response.data.data) {
        const supplier = response.data.data;
        setCurrentSupplier(supplier);
        
        setEditForm({
          name: supplier.name || '',
          email: supplier.email || '',
          phone: supplier.phone || '',
          contactPersonName: supplier.contactPerson?.name || '',
          departmentName: supplier.department?.name || '',
          taxIdentificationNumber: supplier.taxIdentificationNumber || ''
        });
        
        setEditModalVisible(true);
      } else {
        toastify(response.error || 'Failed to fetch supplier details', 'error');
      }
    } catch (error) {
      toastify('Error fetching supplier details', 'error');
      console.error('Error:', error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditSupplier = (id) => {
    fetchSupplierForEdit(id);
  };

  const handleArchiveSupplier = (id) => {
    confirmDialog({
      message: 'Archive this supplier?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          setLoading(true);
          // single archive endpoint
          const response = await makeRequest2(
            `/api/app/suppliers/archive/${selectedFacilityId}`,
            'PUT',
            { supplierIds: [id] }
          );
          if (response.success) {
            toastify(response.message || 'Supplier archived successfully', 'success');
            fetchSuppliers();
          } else {
            toastify(response.error || 'Failed to archive supplier', 'error');
          }
        } catch (error) {
          toastify('Error archiving supplier', 'error');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Edit form field change handler
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle document file change
  const handleDocumentChange = (documentType, file) => {
    setDocumentFiles(prev => ({
      ...prev,
      [documentType]: file
    }));
  };

  // Submit edit form
  const handleEditSubmit = async () => {
    try {
      // Basic validation
      if (!editForm.name.trim()) {
        toastify("Supplier Name is required", "error");
        return;
      }

      if (!editForm.email.trim()) {
        toastify("Email is required", "error");
        return;
      }

      if (!editForm.phone.trim()) {
        toastify("Phone number is required", "error");
        return;
      }

      setEditLoading(true);

      // Prepare form data for file uploads
      const formData = new FormData();
      formData.append("name", editForm.name);
      formData.append("email", editForm.email);
      formData.append("phone", editForm.phone);
      formData.append("contactPerson[name]", editForm.contactPersonName);
      formData.append("department[name]", editForm.departmentName);
      formData.append("taxIdentificationNumber", editForm.taxIdentificationNumber);

      // Append any new document files
      let docIndex = 0;
      for (const [docType, file] of Object.entries(documentFiles)) {
        if (file) {
          formData.append(`documents`, file);
          formData.append(`documentName_${docIndex}`, docType);
          formData.append(`documentType_${docIndex}`, docType.toLowerCase());
          docIndex++;
        }
      }

      const response = await makeRequest2(
        `${updateSupplierURL}/${selectedFacilityId}/${currentSupplier._id}`,
        "PUT",
        formData,
        { "Content-Type": "multipart/form-data" }
      );

      if (!response.success) {
        toastify(response.error || "Failed to update supplier", "error");
        return;
      }

      toastify(response.message || "Supplier updated successfully", "success");
      setEditModalVisible(false);
      fetchSuppliers();
    } catch (error) {
      toastify(
        "An unexpected error occurred: " + (error.message || "Unknown error"),
        "error"
      );
    } finally {
      setEditLoading(false);
    }
  };

  // If no facility is selected, show a prompt
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            Please select a facility to view suppliers.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header with Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Suppliers</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5>Suppliers Management</h5>
            </div>
            <ConfirmDialog />
            <div className="card-body">
              {/* Search and Add Button */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Search by name, email, department..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-8 text-end">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/facility/procurement_management/supplier/add')}
                  >
                    Add Supplier
                  </button>
                  <button
                    className="btn btn-outline-success ms-2"
                    onClick={() => console.log('Export suppliers clicked')}
                  >
                    Export Suppliers
                  </button>
                </div>
              </div>

              {/* Status Filter Buttons */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2">
                    {statusOptions.map(option => (
                      <button
                        key={option.value}
                        className={`btn ${
                          activeTab === option.value ? 'btn-primary' : 'btn-outline-secondary'
                        } text-uppercase`}
                        onClick={() => handleStatusFilterChange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                    {/* <div className="dropdown">
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary dropdown-toggle" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                      >
                        MORE
                      </button>
                      <ul className="dropdown-menu">
                        <li>
                          <button type="button" className="dropdown-item">
                            Rejected
                          </button>
                        </li>
                        <li>
                          <button type="button" className="dropdown-item">
                            Blacklisted
                          </button>
                        </li>
                      </ul>
                    </div> */}
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="table-responsive">
                <DataTable
                  value={filteredSuppliers}
                  emptyMessage="No suppliers found."
                  paginator
                  rows={pagination.limit}
                  totalRecords={pagination.total}
                  stripedRows
                  loading={loading}
                  tableStyle={{ minWidth: '50rem' }}
                  sortMode="multiple"
                  rowHover
                  stateStorage="session"
                  stateKey="supplier-state"
                >
                  {/* Updated columns to match the API response */}
                  <Column field="name" header="SUPPLIER NAME" sortable />
                  <Column field="email" header="EMAIL ADDRESS" sortable />
                  <Column header="CONTACT PERSON" body={contactPersonTemplate} sortable sortField="contactPerson.name" />
                  <Column field="phone" header="PHONE" sortable />
                  <Column header="STATUS" body={statusTemplate} sortable sortField="status" />

                  {/* Actions Column */}
                  <Column
                    header="OPTIONS"
                    body={actionTemplate}
                    style={{ width: '10rem' }}
                  />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Supplier Modal */}
      <Dialog
        header="Edit Supplier"
        visible={editModalVisible}
        onHide={() => setEditModalVisible(false)}
        style={{ width: '80vw' }}
        breakpoints={{ '960px': '75vw', '640px': '100vw' }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setEditModalVisible(false)}
              disabled={editLoading}
            />
            <Button
              label={editLoading ? "Saving..." : "Save"}
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleEditSubmit}
              disabled={editLoading}
            />
          </div>
        }
      >
        {editLoading && (
          <div className="text-center p-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {!editLoading && currentSupplier && (
          <div className="container">
            <div className="row">
              {/* Supplier Name */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  Supplier Name<span style={{ color: "red" }}>*</span>
                </label>
                <InputText
                  className="form-control"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                  placeholder="Enter supplier name"
                  disabled={editLoading}
                />
              </div>

              {/* Email Address */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  Email Address<span style={{ color: "red" }}>*</span>
                </label>
                <InputText
                  className="form-control"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditFormChange}
                  placeholder="Enter email address"
                  disabled={editLoading}
                />
              </div>

              {/* Phone Number */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">
                  Phone Number<span style={{ color: "red" }}>*</span>
                </label>
                <InputText
                  className="form-control"
                  name="phone"
                  value={editForm.phone}
                  onChange={handleEditFormChange}
                  placeholder="Enter phone number"
                  disabled={editLoading}
                />
              </div>

              {/* Tax Identification Number */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">Tax Identification Number</label>
                <InputText
                  className="form-control"
                  name="taxIdentificationNumber"
                  value={editForm.taxIdentificationNumber}
                  onChange={handleEditFormChange}
                  placeholder="Enter tax identification number"
                  disabled={editLoading}
                />
              </div>

              {/* Contact Person Name */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">Contact Person Name</label>
                <InputText
                  className="form-control"
                  name="contactPersonName"
                  value={editForm.contactPersonName}
                  onChange={handleEditFormChange}
                  placeholder="Enter contact person name"
                  disabled={editLoading}
                />
              </div>

              {/* Department Name */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">Department Name</label>
                <InputText
                  className="form-control"
                  name="departmentName"
                  value={editForm.departmentName}
                  onChange={handleEditFormChange}
                  placeholder="Enter department name"
                  disabled={editLoading}
                />
              </div>

              {/* Documents Section */}
              <div className="col-sm-12 mb-3">
                <h6 className="mb-3">Update Documents</h6>
                <p className="text-muted">Upload new documents only if you want to replace existing ones.</p>
              </div>

              {/* Document Uploads */}
              <div className="col-sm-6 mb-3">
                <label className="form-label">Pin Certificate</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => handleDocumentChange('Pin Certificate', e.target.files[0])}
                  disabled={editLoading}
                />
              </div>

              <div className="col-sm-6 mb-3">
                <label className="form-label">Supplier Registration Form</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => handleDocumentChange('Supplier Registration Form', e.target.files[0])}
                  disabled={editLoading}
                />
              </div>

              <div className="col-sm-6 mb-3">
                <label className="form-label">Certificate of Incorporation</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => handleDocumentChange('Certificate of Incorporation', e.target.files[0])}
                  disabled={editLoading}
                />
              </div>

              <div className="col-sm-6 mb-3">
                <label className="form-label">Directors ID</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(e) => handleDocumentChange('Directors ID', e.target.files[0])}
                  disabled={editLoading}
                />
              </div>

              {/* Existing Documents */}
              {currentSupplier.documents && currentSupplier.documents.length > 0 && (
                <div className="col-sm-12 mt-3">
                  <h6>Current Documents</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mt-2">
                      <thead className="table-light">
                        <tr>
                          <th>Document Name</th>
                          <th>Type</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentSupplier.documents.map((doc, index) => (
                          <tr key={index}>
                            <td>{doc.documentName || 'N/A'}</td>
                            <td>
                              {doc.documentType ? 
                                doc.documentType.charAt(0).toUpperCase() + doc.documentType.slice(1) 
                                : 'N/A'}
                            </td>
                            <td>
                              {doc.documentUrl && (
                                <a 
                                  href={doc.documentUrl} 
                                  target="_blank" 
                                  rel="noreferrer" 
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View Document
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </Layout>
  );
};

export default SuppliersManagement;